/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';

import { messages } from './messages';

const REINTRO_VIDEO_SRC = 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OAWR.mp4';

/**
 * Must match `HERO_BACKGROUND_COLOR` in the modal: the gradient fades the video's
 * left edge into the hero background. Kept as a local copy because
 * `@atlaskit/ui-styling-standard` forbids importing values used inside `cssMap`.
 */
const HERO_BACKGROUND_COLOR = '#F8F8F8';

const styles = cssMap({
	// Dimensions and offsets mirror the Figma spec: the prompt box bleeds past the
	// right and bottom edges of the hero, which clips the overflow. The inset
	// properties only accept space tokens, so anchor to the bottom-right corner
	// and use a transform (untyped) to push the box out by the bleed amount.
	container: {
		position: 'absolute',
		right: 0,
		bottom: 0,
		zIndex: 1,
		transform: 'translate(89px, 4px)',
		// Cap at the Figma width but scale down on narrow viewports / high zoom so the
		// video never exceeds the hero (the parent clips overflow, but a fixed px width
		// could still force unexpected layout on small screens).
		width: 'min(541px, 90%)',
		aspectRatio: '105 / 59',
	},
	video: {
		display: 'block',
		width: '100%',
		height: '100%',
		objectFit: 'contain',
	},
	// Gradient block that sits over the left edge of the video and fades it into
	// the hero background, blending the clip with the container (Figma spec).
	blend: {
		position: 'absolute',
		insetBlockStart: 0,
		insetBlockEnd: 0,
		insetInlineStart: 0,
		width: '90px',
		pointerEvents: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		backgroundImage: `linear-gradient(90deg, ${HERO_BACKGROUND_COLOR} 0%, transparent 100%)`,
	},
});

/**
 * Decorative, looping video of the Rovo prompt box shown in the hero of the
 * modal. Autoplays muted so it can start without user interaction. Positioned
 * absolutely, so it must be rendered inside a `position: relative` container.
 */
export function PromptBoxVideo(): JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<div css={styles.container}>
			{/* eslint-disable-next-line jsx-a11y/media-has-caption -- decorative, silent looping clip */}
			<video
				css={styles.video}
				autoPlay
				loop
				muted
				playsInline
				aria-label={formatMessage(messages.chatPreviewPrompt)}
			>
				<source src={REINTRO_VIDEO_SRC} type="video/mp4" />
			</video>
			<div css={styles.blend} aria-hidden="true" />
		</div>
	);
}
